import { useContext, useEffect, useState } from 'react';

import GroupedLoadingContext from 'contexts/GroupedLoadingContext';
import PRINT_LOADING_SELECTOR from 'screens/PrintReportShow/printLoadingSelector';

// Poll interval for the "fully rendered" check.
const POLL_MS = 700;
// Absolute safety net so we always signal rather than hanging to the renderer's
// wait timeout.
const MAX_WAIT_MS = 90000;

/**
 * Whether a headless print screen has finished rendering, for the renderer's
 * data-print-ready marker.
 *
 * Ready once the content has loaded AND there are no loading spinners, gadget
 * skeletons (see PRINT_LOADING_SELECTOR) or pending grid refits left in the
 * DOM. Requires it stable across a few polls, then waits for fonts + two
 * paints. A hard cap guarantees it always signals rather than hanging.
 *
 * Changing `resetKey` (e.g. the wallboard slide being shown) starts the check
 * again for the new content.
 */
const usePrintReady = ({
  isContentReady,
  resetKey = '',
}: {
  isContentReady: boolean;
  resetKey?: string;
}): boolean => {
  const { isLoading: dataLoading } = useContext(GroupedLoadingContext);
  const [readyKey, setReadyKey] = useState<string | undefined>();
  const isReady = readyKey === resetKey;

  useEffect(() => {
    if (isReady || !isContentReady) {
      return;
    }
    let cancelled = false;
    let stable = 0;
    const finish = async () => {
      if (document.fonts?.ready) {
        await document.fonts.ready.catch(() => undefined);
      }
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      if (!cancelled) setReadyKey(resetKey);
    };
    const poll = setInterval(() => {
      const stillLoading =
        dataLoading ||
        document.querySelectorAll(PRINT_LOADING_SELECTOR).length > 0 ||
        // Wait for grid cards to finish refitting to their content (see
        // ReportCanvas buildHeadlessRefitLayout) before capturing.
        document.querySelectorAll('[data-headless-refit-pending="true"]')
          .length > 0;
      if (stillLoading) {
        stable = 0;
        return;
      }
      stable += 1;
      if (stable >= 3) {
        clearInterval(poll);
        finish();
      }
    }, POLL_MS);
    const cap = setTimeout(() => {
      clearInterval(poll);
      if (!cancelled) setReadyKey(resetKey);
    }, MAX_WAIT_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(cap);
    };
  }, [isReady, isContentReady, dataLoading, resetKey]);

  return isReady;
};

export default usePrintReady;
